import {AdminStore} from "./admin_store";
import {makeAutoObservable} from "mobx";
import {proto} from "../../proto/messages";
import {admin_participants, cvv_types, exports} from "../../proto/compiled";
import {SocketApiAckStatus} from "proto_socket_typescript";
import {toast} from "react-toastify";
import {appStore} from "../../index";
import {YesNoDialog} from "../../components/dialogs";
import {CvvTheme} from "../../theme";
import {debounce, isNaN} from "lodash";
import {ErrorUtils} from "../../utils/error_utils";

export class AdminParticipantsStore {
  private disposeCallbacks: (() => any)[] = [];
  base: AdminStore;
  page = 0;
  search?: string;
  date?: Date;
  sortBy: string = '-date_created';  // prefix with '-' for descending

  toggleSort(field: string) {
    if (this.sortBy === `-${field}`) {
      this.sortBy = field;  // switch to ascending
    } else if (this.sortBy === field) {
      this.sortBy = `-${field}`;  // switch to descending
    } else {
      this.sortBy = `-${field}`;  // new field, default descending
    }
    this.loadParticipants();
  }

  getSortDirection(field: string): 'asc' | 'desc' | null {
    if (this.sortBy === `-${field}`) return 'desc';
    if (this.sortBy === field) return 'asc';
    return null;
  }

  // Pure version of toggleSort's direction logic: returns the next sortBy value for
  // `field` without mutating or loading. The page writes the result to the URL, which
  // then flows back into `this.sortBy` and triggers the reload.
  nextSortBy(field: string): string {
    if (this.sortBy === `-${field}`) return field;   // desc -> asc
    if (this.sortBy === field) return `-${field}`;   // asc -> desc
    return `-${field}`;                              // new field -> desc
  }
  participants?: admin_participants.IAdminParticipants;
  participant?: admin_participants.IAdminParticipant;
  commentTypes?: cvv_types.ICommentType[];
  newComment: cvv_types.Comment = new cvv_types.Comment({
    type: new cvv_types.CommentType()
  });
  comments?: cvv_types.IComment[];

  constructor(base: AdminStore) {
    this.base = base;
    makeAutoObservable(this);
    makeAutoObservable(this.newComment);
    makeAutoObservable(this.newComment.type!);

    this.onSub(
      this.base.api.getMessageHandler(new proto.RxAdminParticipants()).subscribe((m) => this.onAdminParticipants(m))
    );
    this.onSub(
      this.base.api.getMessageHandler(new proto.RxAdminParticipant()).subscribe((m) => this.onAdminParticipant(m))
    );
    this.onSub(
      this.base.api.getMessageHandler(new proto.RxAdminCommentTypes()).subscribe((m) => this.onAdminCommentTypes(m))
    );
    this.onSub(
      this.base.api.getMessageHandler(new proto.RxAdminParticipantComments()).subscribe((m) => this.onAdminComments(m))
    );
  }

  load(): void {
    this.participant = undefined;
    this.base.api.sendMessage(proto.TxAdminLoadCommentTypes.create())
    this.loadParticipants();
  }

  // Comment types are page-wide reference data (not a filter), so the URL-driven
  // page loads them once on mount instead of going through load().
  loadCommentTypes(): void {
    this.base.api.sendMessage(proto.TxAdminLoadCommentTypes.create());
  }

  setPage(page: number): boolean {
    if (isNaN(page) || page > (this.participants?.nPages ?? 0) || page < 0) return false;
    if (page === this.page) return true;
    this.page = page;
    this.loadParticipantsDebounced();
    return true;
  }

  private loadFilters(): admin_participants.IAdminLoadParticipants {
    return {
      page: this.page,
      tecajTipKey: this.base.tecajTip,
      tecajKey: this.base.tecaj,
      tecajId: this.base.razpisaniTecaj?.razpisaniTecajId,
      search: this.search,
      extraConditions: this.base.dodatniPogoji,
      extraPaymentConditions: this.base.dodatniPlacilniPogoji,
      date: this.date && new cvv_types.Date({ts: this.date.getTime()}),
      sortBy: this.sortBy,
    };
  }

  async loadParticipants() {
    return await this.base.api.sendMessage(proto.TxAdminLoadParticipants.create(this.loadFilters()), {ack: true});
  }

  loadParticipantsDebounced = debounce(() => this.loadParticipants(), 300, {leading: true, trailing: true});

  async togglePresence(participant: admin_participants.IParticipantSummary, dayN: number) {
    const message = proto.TxAdminParticipantSetPresence.create({
      sessionId: participant.sessionId,
      daysPresent: Array.from(participant.daysPresent!)
    });

    const dI = message.proto.daysPresent!.indexOf(dayN);
    if (dI < 0) {
      message.proto.daysPresent.push(dayN);
    } else {
      const result = await new Promise((resolve, reject) => {
        appStore.showDialog(
          <YesNoDialog result={(result) => {
            appStore.hideDialog();
            resolve(result);
          }}>
            <CvvTheme.textTheme.headline5.span>Ali ste prepričani, da želite preklicati prisotnost na
              tečaju?
            </CvvTheme.textTheme.headline5.span>
          </YesNoDialog>
        );
      });
      if (!result) return;
      message.proto.daysPresent.splice(dI, 1)
    }
    const response = await this.base.api.sendMessage(message, {ack: true});
    if (response.status === SocketApiAckStatus.success) {
      participant.daysPresent = message.proto.daysPresent;
      if (this.participant?.summary && this.participant?.summary?.sessionId === participant.sessionId) {
        this.participant.summary.daysPresent = message.proto.daysPresent;
      }
    } else {
      toast.error(response.errorMessage ?? 'Neznana napaka');
    }
  }

  dispose() {
    for (const dc of this.disposeCallbacks) {
      dc();
    }
  }

  private onSub(subscription: any) {
    this.disposeCallbacks.push(() => subscription.unsubscribe());
  }

  private onAdminParticipants(message: proto.RxAdminParticipants) {
    this.participants = message.proto;
  }

  private onAdminParticipant(message: proto.RxAdminParticipant) {
    this.participant = message.proto;
    makeAutoObservable(this.participant!.summary!);
    makeAutoObservable(this.participant!.form!);
    makeAutoObservable(this.participant!.form!.step1!);
    makeAutoObservable(this.participant!.form!.step2!);
    makeAutoObservable(this.participant!.form!.step3!);
    if (this.participant!.form!.step3!.datumIzdajeVozniske) {
      makeAutoObservable(this.participant!.form!.step3!.datumIzdajeVozniske!);
    }
    if (this.participant!.form!.step3!.datumOpravljenegaIntenzivnegaTecaja) {
      makeAutoObservable(this.participant!.form!.step3!.datumOpravljenegaIntenzivnegaTecaja!);
    }
    makeAutoObservable(this.participant!.form!.step4!);
    makeAutoObservable(this.participant!.form!.step5!);
    makeAutoObservable(this.participant!.form!.step6!);
  }

  setParticipant(id?: string) {
    if (!id) {
      this.participant = undefined;
    } else {
      this.base.api.sendMessage(proto.TxAdminLoadParticipant.create({
        id: id,
      }));
    }

  }

  private onAdminCommentTypes(message: proto.RxAdminCommentTypes) {
    this.commentTypes = message.proto.types;
    if (this.commentTypes?.length) {
      this.newComment.type!.id = this.commentTypes?.find(t => t.userCanAdd)?.id;
    }
  }

  async saveNewComment() {
    const response = await this.base.api.sendMessage(proto.TxAdminParticipantSaveComment.create({
      sessionId: this.participant?.summary?.sessionId,
      comment: this.newComment
    }), {ack: true});
    this.newComment.text = '';
    if (response.status !== SocketApiAckStatus.success) {
      toast.error(response.errorMessage ?? 'Neznana napaka');
    }
  }

  async resendConfirmationMail() {
    const response = await this.base.api.sendMessage(proto.TxAdminParticipantResendConfirmationMail.create({
      sessionId: this.participant?.summary?.sessionId
    }), {ack: true});
    if (response.status === SocketApiAckStatus.success) {
      toast.success('Poslano');
    } else {
      toast.error(response.errorMessage ?? 'Neznana napaka');
    }
  }

  private onAdminComments(m: proto.RxAdminParticipantComments) {
    this.comments = m.proto.comments;
  }

  async deleteComment(comment: cvv_types.IComment) {
    const response = await this.base.api.sendMessage(proto.TxAdminParticipantDeleteComment.create({
      commentId: comment.id
    }), {ack: true})
    if (response.status !== SocketApiAckStatus.success) {
      toast.error(response.errorMessage ?? 'Neznana napaka');
    } else {
      await this.base.api.sendMessage(proto.TxAdminLoadParticipant.create({
        id: this.participant?.summary?.sessionId,
      }));
      await this.loadParticipants();
    }
  }

  async archive(participant: admin_participants.IAdminParticipant) {
    const response = await this.base.api.sendMessage(proto.TxAdminArchiveParticipant.create({
      sessionId: participant.summary?.sessionId
    }), {ack: true});
    if (response.status !== SocketApiAckStatus.success) {
      toast.error(response.errorMessage ?? 'Neznana napaka');
    } else {
      await this.base.api.sendMessage(proto.TxAdminLoadParticipant.create({
        id: participant?.summary?.sessionId,
      }));
      this.loadParticipants();
    }
  }

  async downloadConfirmation(exportType?: exports.ExportType) {
    const response = await this.base.api.sendMessage(proto.TxExportSession.create({
      sessionId: this.participant?.summary?.sessionId,
      exportType: exportType ?? exports.ExportType.potrdilo,
    }), {ack: true});
    if (response.status !== SocketApiAckStatus.success) {
      toast.error(response.errorMessage ?? 'Neznana napaka');
    }
  }

  async createInvoice() {
    const response = await this.base.api.sendMessage(proto.TxAdminCreateInvoice.create({
      sessionId: this.participant?.summary?.sessionId,
    }), {ack: true});
    if (response.status !== SocketApiAckStatus.success) {
      toast.error(response.errorMessage ?? 'Neznana napaka');
    } else {
      this.setParticipant(this.participant?.summary?.sessionId!);
    }
  }

  async regenerateAdvance() {
    const response = await this.base.api.sendMessage(proto.TxAdminRegenerateAdvance.create({
      sessionId: this.participant?.summary?.sessionId,
    }), {ack: true});
    if (response.status !== SocketApiAckStatus.success) {
      toast.error(response.errorMessage ?? 'Neznana napaka');
    } else {
      this.setParticipant(this.participant?.summary?.sessionId!);
    }
  }

  async exportParticipants() {
    const toastId = toast.info('Izvoz podatkov je v teku, prenos se bo začel v nekaj sekundah.');
    const response = await this.base.api.sendMessage(proto.TxAdminExportParticipants.create({
      filters: {
        ...this.loadFilters(),
        page: undefined,
      }
    }));
    toast.dismiss(toastId);
    if (response.status !== SocketApiAckStatus.success) {
      toast.error(response.errorMessage ?? 'Neznana napaka');
    } else {
      toast.success('Izvoz je pripravljen.');
    }
  }

  async downloadInvoicePdf(invoiceType: admin_participants.InvoiceType) {
    try {
      const pdfResponse = await this.base.api.fetch(
        proto.TxAdminGetInvoicePdf.create({
          sessionId: this.participant?.summary?.sessionId,
          invoiceType: invoiceType,
        }),
        new proto.RxAdminInvoicePdf(),
        {timeoutMs: 30000}
      );

      if (pdfResponse?.proto?.pdfBase64) {
        // Convert base64 to blob and trigger download
        const byteCharacters = atob(pdfResponse.proto.pdfBase64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], {type: 'application/pdf'});
        const url = URL.createObjectURL(blob);

        // Generate filename
        const invoiceName = invoiceType === admin_participants.InvoiceType.INVOICE_TYPE_ADVANCE
          ? this.participant?.summary?.navInvoiceAdvance
          : this.participant?.summary?.navInvoice;
        const filename = `${invoiceName || 'racun'}.pdf`;

        // Trigger download
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } else {
        toast.error('Napaka pri pridobivanju PDF');
      }
    } catch (e) {
      ErrorUtils.fromFetch(e);
    }
  }
}